<template>
  <div class="d-flex flex-wrap h-100 w-100">
    <div class="col-6 d-flex h-100 w-100">
      <b-overlay :show="editing" :opacity="0.95" blur="5px" variant="transparent" class="d-flex flex-column h-100 w-100">
        <template #overlay>
          <div class="text-center">
            <i class="far fa-edit fa-3x"></i>
          </div>
        </template>
        <div class="d-flex flex-wrap justify-content-end">
          <b-button variant="third" class="m-1" @click="onNew"><i class="fas fa-plus"></i></b-button>
          <b-button variant="third" class="m-1" @click="onLoad"><i class="fas fa-redo-alt"></i></b-button>
        </div>
        <ag-grid-vue id="pcidic-grid"
                    class="ag-theme-alpine w-100 d-flex flex-column flex-grow-1"
                    rowSelection="single"
                    :getLocaleText="localeTextFunc"
                    :gridOptions="itemGridOptions"
                    :columnTypes="columnTypes"
                    :columnDefs="itemColumnDefs"
                    :rowData="objList"
                    :getRowId="params => params.data.id"
                    :undoRedoCellEditing="true"
                    :undoRedoCellEditingLimit="1"
                    @cellDoubleClicked="onSelectRow"
                    @cellValueChanged="onChangeStatus"
                    @gridReady="onGridReady"
                    @firstDataRendered="onGridReady">
        </ag-grid-vue>
      </b-overlay>
    </div>
    <div v-if="editing" class="col-6 d-flex h-100 w-100">
      <b-card class="w-100 h-100 m-0 t-0">
        <template #header>
          <div class="d-flex">
            <div class="flex-grow-1"><i class="fas fa-edit text-thrid mr-1"></i><strong>{{ $lang.PCI.COPItem.Title }}</strong></div>
            <div class="card-actions">
              <b-link href="#" v-b-tooltip.hover :title="$lang.Common.Buttons.Save" @click="onSaveForm">
                <i class="fas fa-check" style="font-size:1rem;color:var(--success);"></i>
              </b-link>
              <b-link href="#" v-if="editing" v-b-tooltip.hover :title="$lang.Common.Buttons.Cancel" @click="onCancel">
                <i class="fas fa-times" style="font-size:1rem;color:var(--success);"></i>
              </b-link>
              <b-dropdown href="#"
                          v-if="canDelete && data.id"
                          variant="link"
                          class="h-100"
                          toggle-class="text-decoration-none p-0"
                          no-caret
                          tabindex="-1"
                          v-b-tooltip.hover
                          :title="$lang.Common.Tooltip.ButtonDelete">
                <template #button-content>
                  <i class="fa fa-trash text-danger p-1" style="cursor:pointer"></i>
                </template>
                <b-dropdown-header><strong>{{$lang.Common.ConfirmDelete}}</strong></b-dropdown-header>
                <b-dropdown-item href="#" v-on:click="onDelete">{{$lang.Common.Yes}}</b-dropdown-item>
                <b-dropdown-item href="#">{{$lang.Common.No}}</b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
        </template>
        <div class="d-flex flex-wrap">
          <div class="col-4">
            <b-form-group :label="$lang.PCI.COPItem.LabelType">
              <v-select v-model="form.tipo.value" :required="form.tipo.required" :state="form.tipo.state" :options="form.tipo.options" :reduce="param => param.tipo"></v-select>
            </b-form-group>
          </div>
          <div class="col-8">
            <b-form-group :label="$lang.PCI.COPItem.LabelDescription">
              <b-form-input v-model="form.descricao.value" :required="form.descricao.required" :state="form.descricao.state"></b-form-input>
            </b-form-group>
          </div>
          <div class="col-2 mt-3">
            <b-form-checkbox switch v-model="form.itemInicio.value" :required="form.itemInicio.required" :state="form.itemInicio.state">{{ $lang.PCI.COPItem.LabelStartItem }}</b-form-checkbox>
          </div>
          <div class="col-2 mt-3">
            <b-form-checkbox switch v-model="form.itemFim.value" :required="form.itemFim.required" :state="form.itemFim.state">{{ $lang.PCI.COPItem.LabelEndItem }}</b-form-checkbox>
          </div>
          <div class="col-2 mt-3">
            <b-form-checkbox switch v-model="form.dfd.value" :required="form.dfd.required" :state="form.dfd.state">{{ 'DFD' }}</b-form-checkbox>
          </div>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import Tools from '@/components/tools/Tools'
import { AgGridVue } from 'ag-grid-vue'
import gridMixin from '../mixins/gridMixin'
import COPItemRepository from '@/shared/http/repositories/pci/copitem'
import CompanyMixin from '@/store/module/company/mixin'
import { loading, toast } from '@/shared/utils/UIHelper'
import roles from '@/shared/utils/roles'
import PermisionMixin from '@/store/module/permission/mixin'
import actionsForm from '@/shared/mixins/actionsForm'
import copitemForm from './copitem-form'
import copitemmodel from '@/shared/models/pci/copitemmodel'
import ActionCellRenderer from './actionCellRenderer.vue'
export default {
  name: 'CopItem',
  components: {
    AgGridVue,
    ActionCellRenderer
  },
  mixins: [gridMixin, CompanyMixin, PermisionMixin, actionsForm],
  data () {
    return {
      objList: [],
      form: copitemForm,
      data: copitemmodel,
      oldData: null,
      editing: false,
      roles: roles,
      itemGridOptions: {
        headerHeight: 40,
        rowHeight: 30,
        defaultColDef: {
          filter: 'agSetColumnFilter',
          resizable: true,
          editable: false,
          suppressMenu: false,
          sortable: true,
          menuTabs: ['filterMenuTab'],
          suppressMovable: true,
          lockVisible: true,
          minWidth: 30
        }
      },
      itemColumnDefs: [
        {
          headerName: this.$lang.PCI.COPItem.LabelCode,
          field: 'codigo',
          type: 'integerColumn',
          width: 10
        },
        {
          headerName: this.$lang.PCI.COPItem.LabelType,
          valueGetter: params => {
            return this.$lang.PCI.COPItem['Type' + params.data.tipo]
          },
          width: 100
        },
        {
          headerName: this.$lang.PCI.COPItem.LabelDescription,
          field: 'descricao',
          type: 'textColumn',
          width: 200
        },
        {
          headerName: 'DFD',
          field: 'dfd',
          type: 'booleanColumn',
          width: 10
        },
        {
          headerName: 'Status',
          cellRenderer: 'ActionCellRenderer',
          cellRendererParams: {
            onChangeStatus: this.onChangeStatus,
            disabled: () => !this.canDelete
          },
          filterValueGetter: params => params.data.status === 1 ? 'Ativo' : 'Inativo',
          width: 50
        }
      ]
    }
  },
  beforeMount () {
    Tools.MinimizedSidebar()
  },
  computed: {
    canDelete () {
      return this.hasRole && this.hasRole(roles.RW_OPER_PCI_COP_ITEM_DEL)
    }
  },
  methods: {
    onLoad () {
      this.form.tipo.options = [
        {tipo: 1, label: this.$lang.PCI.COPItem.Type1},
        {tipo: 2, label: this.$lang.PCI.COPItem.Type2}
      ]
      loading.push()
      COPItemRepository.GetAll(this.company.id, {params: {inativos: true}})
        .then(response => {
          if (response.data.code > 0) {
            this.objList = response.data.data
            this.$nextTick(() => {
              this.itemGridOptions.api.refreshCells({force: true})
            })
          } else throw new Error(response.data.message)
        })
        .catch(err => {
          console.log(err)
          toast.error(this.$lang.Common.Alerts.Error)
        })
        .finally(() => loading.close())
    },
    onNew () {
      this.onClear()
      this.editing = true
    },
    onCancel () {
      this.onClear()
      this.editing = false
    },
    onChangeStatus (params) {
      this.onEdit(params.data)
      this.form.status.value = params.data.status ? 0 : 1
      this.onSave()
    },
    onCreate () {
      loading.push()
      this.data.empresaid = this.company.id
      this.data.codigo = 0
      COPItemRepository.Create(this.data)
        .then(response => {
          if (response.data.code > 0) {
            this.objList.push(response.data.data)
            this.editing = false
            toast.success(this.$lang.Common.Alerts.Success)
          } else {
            throw new Error(response.data.message)
          }
        })
        .catch(err => {
          this.onException(err)
        })
        .finally(() => loading.close())
    },
    onUpdate () {
      loading.push()
      this.data.empresaid = this.company.id
      COPItemRepository.Update(this.data)
        .then(response => {
          if (response.data.code > 0) {
            const idx = this.objList.findIndex(d => d.id === response.data.data.id)
            this.objList.splice(idx, 1, response.data.data)
            this.editing = false
            toast.success(this.$lang.Common.Alerts.Success)
          } else {
            throw new Error(response.data.message)
          }
        })
        .catch(err => {
          this.onException(err)
        })
        .finally(() => loading.close())
    },
    onSaveForm () {
      this.form.itemInicio.value = this.form.itemInicio.value === null ? false : this.form.itemInicio.value
      this.form.itemFim.value = this.form.itemFim.value === null ? false : this.form.itemFim.value
      this.form.dfd.value = this.form.dfd.value === null ? false : this.form.dfd.value
      this.onSave()
    },
    onDelete () {
      loading.push()
      COPItemRepository.Delete(this.data.id)
        .then(response => {
          if (response.data.code > 0) {
            const idx = this.objList.findIndex(d => d.id === response.data.data.id)
            this.objList.splice(idx, 1)
            this.editing = false
            toast.success(this.$lang.Common.Alerts.Success)
          } else {
            throw new Error(response.data.message)
          }
        })
        .catch(err => {
          this.onException(err)
        })
        .finally(() => loading.close())
    },
    onSelectRow (params) {
      if (params.node.selected) {
        this.editing = true
        this.onEdit(params.data)
      }
    },
    onGridReady (params) {
      setTimeout(() => {
        params.api.sizeColumnsToFit()
      }, 500)
    }
  }
}
</script>

<style scoped>
i {
  font-size: 1rem;
}
</style>



// WEBPACK FOOTER //
// src/views/private/operational/pci/copitem/copitem.vue